import {
  toFiniteNumber as contractToFiniteNumber,
  toFiniteNumberOrZero as contractToFiniteNumberOrZero,
  toNumberOrUndefined as contractToNumberOrUndefined,
  toPositiveInteger as contractToPositiveInteger,
} from '@sweetistics/contracts/utils/type-coercion';

import { ensureDateOrNull, isDateCoercible } from './date-coercion';
import { toTrimmedNonEmptyString } from './type-guards';

export function toFiniteNumber(value: number | null | undefined): number | undefined {
  return contractToFiniteNumber(value);
}

export function toFiniteNumberOrZero(value: number | null | undefined): number {
  return contractToFiniteNumberOrZero(value);
}

export function toPositiveInteger(value: number | null | undefined): number | undefined {
  return contractToPositiveInteger(value);
}

export const toNumberOrUndefined = contractToNumberOrUndefined;

export function toNumber(value: unknown, fallback?: number): number | undefined {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : fallback;
  }
  if (typeof value === 'bigint') {
    return Number(value);
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed.length === 0) {
      return fallback;
    }
    const parsed = Number(trimmed);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return fallback;
}

export function toInteger(value: unknown, fallback = 0): number {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return Math.floor(value);
  }
  if (typeof value === 'string') {
    const parsed = Number.parseInt(value, 10);
    if (!Number.isNaN(parsed)) {
      return parsed;
    }
  }
  return fallback;
}

export function toStringOrNull(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

export function toStringValue(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

export function toNumberOrNull(value: unknown): number | null {
  const numeric = toNumberOrUndefined(value);
  return numeric ?? null;
}

export function toBoolean(value: unknown): boolean | null {
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'true' || normalized === '1' || normalized === 'yes') {
      return true;
    }
    if (normalized === 'false' || normalized === '0' || normalized === 'no' || normalized === '') {
      return false;
    }
  }
  if (typeof value === 'number') {
    return value !== 0;
  }
  return null;
}

export function toBooleanOrFallback(value: unknown, fallback = false): boolean {
  const result = toBoolean(value);
  return result === null ? fallback : result;
}

export function toBooleanIfBoolean(value: unknown): boolean | undefined {
  return typeof value === 'boolean' ? value : undefined;
}

export function toTrueFlag(value: unknown): boolean {
  return value === true;
}

export function toIsoStringOrUndefined(value: unknown): string | undefined {
  try {
    if (isDateCoercible(value)) {
      const parsed = ensureDateOrNull(value);
      if (parsed) {
        return parsed.toISOString();
      }
    }
  } catch {
    // Swallow normalization errors and fall back to manual string checks.
  }
  const candidate = toTrimmedNonEmptyString(value);
  if (!candidate) {
    return;
  }
  const normalized = ensureDateOrNull(candidate);
  return normalized ? normalized.toISOString() : undefined;
}

export function toIsoStringOrNull(value: unknown): string | null {
  const iso = toIsoStringOrUndefined(value);
  return iso ?? null;
}

export function toBigIntOrNull(value: unknown): bigint | null {
  if (typeof value === 'bigint') {
    return value;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return BigInt(Math.trunc(value));
  }
  const trimmed = toTrimmedNonEmptyString(value);
  if (!trimmed) {
    return null;
  }
  try {
    return BigInt(trimmed);
  } catch {
    return null;
  }
}
